import { useMemo } from 'react';
import { isEmpty } from 'lodash';
import { DocumentReferencesView } from 'api';
import { UseSearchDocumentsProps, UseSearchDocumentsReturn } from './UseSearchDocuments.props';

export const useSearchDocuments = ({
  tableData,
  folderStatusMap,
  searchQuery,
}: UseSearchDocumentsProps): UseSearchDocumentsReturn => {
  const filteredTableData = useMemo(() => {
    if (!searchQuery.trim()) {
      return tableData;
    }

    const query = searchQuery.toLowerCase();
    const filterFiles = (files: DocumentReferencesView[] | undefined) =>
      files?.filter(file => file.file.filename.toLowerCase().includes(query)) ?? [];

    const filteredFolders = tableData.folders
      ?.map(folder => {
        const matchingFiles = filterFiles(folder.files);
        const filteredChildFolders = folder.childFolders
          ?.map(child => ({ ...child, files: filterFiles(child.files) }))
          .filter(child => child.files.length > 0);
        return { ...folder, files: matchingFiles, childFolders: filteredChildFolders };
      })
      .filter(folder => folder.files.length > 0 || (folder.childFolders?.length ?? 0) > 0);

    return { folders: filteredFolders, files: filterFiles(tableData.files) };
  }, [tableData, searchQuery]);

  const effectiveFolderStatusMap = useMemo(() => {
    if (!searchQuery.trim()) {
      return folderStatusMap;
    }

    const map = { ...folderStatusMap };
    filteredTableData.folders?.forEach(folder => {
      if (folder.id) {
        map[folder.id] = { ...map[folder.id], open: true };
      }
      folder.childFolders?.forEach(child => {
        if (child.id) {
          map[child.id] = { ...map[child.id], open: true };
        }
      });
    });
    return map;
  }, [searchQuery, folderStatusMap, filteredTableData]);

  const areFolders = useMemo(() => !isEmpty(filteredTableData.folders), [filteredTableData]);
  const hasFilteredResults = useMemo(
    () => !isEmpty(filteredTableData.files) || !isEmpty(filteredTableData.folders),
    [filteredTableData]
  );

  return { filteredTableData, effectiveFolderStatusMap, areFolders, hasFilteredResults };
};
